/**
 * Route helper utilities for dynamic route generation
 * 
 * These helpers provide type-safe ways to build routes with parameters
 * and query strings while using the ROUTES constants.
 * 
 * IMPORTANT: For auth routes, use the smart auth helpers from
 * @/utils/authRedirect instead of hardcoding signin vs signup.
 */
import { ROUTES } from '@/constants';
import { isReturningVisitor } from '@/utils/authRedirect';

/**
 * Build a route with path parameters replaced
 * @example buildRoute(ROUTES.CHAT_SESSION, { sessionId: '123' }) → '/chat/123'
 */
export function buildRoute(
  route: string, 
  params: Record<string, string>
): string {
  let result = route;
  for (const [key, value] of Object.entries(params)) {
    result = result.replace(`:${key}`, encodeURIComponent(value));
  }
  return result;
}

/**
 * Build a route with query parameters
 * @example buildRouteWithQuery(ROUTES.AUTH, { returnTo: '/account' }) → '/auth?returnTo=%2Faccount'
 */
export function buildRouteWithQuery(
  route: string,
  query: Record<string, string>
): string {
  const params = new URLSearchParams(query);
  return `${route}?${params.toString()}`;
}

/**
 * Convenience functions for common dynamic routes
 * Provides autocomplete and type safety for route building
 */
export const routeTo = {
  /** Navigate to a specific chat session */
  chatSession: (sessionId: string) => 
    buildRoute(ROUTES.CHAT_SESSION, { sessionId }),
  
  /** Navigate to chat with a project filter */
  chatWithProject: (projectId: string) => 
    buildRouteWithQuery(ROUTES.CHAT, { project: projectId }),
  
  /** Navigate to an expert's profile (canonical flat URL) */
  expertProfile: (vanityUrl: string) => `/${vanityUrl}`,
  
  /** Navigate to publication editor for a specific publication */
  publicationEditor: (publicationId: string) => 
    buildRoute(ROUTES.ACCOUNT_PUBLICATION_EDITOR, { publicationId }),
  
  // REMOVED: publicationDetail - publications are accessed via /:vanityUrl/:slug format
  // Use expertPublication(vanityUrl, slug) instead

  /** Navigate to publication detail page */
  publicationDetail: (slug: string) =>
    buildRoute(ROUTES.PUBLICATION_DETAIL, { slug }),
  
  /** Navigate to page editor for a specific page */
  pageEditor: (pageId: string) => 
    buildRoute(ROUTES.ADMIN_MANAGEMENT_PAGE_EDITOR, { pageId }),
  
  /** Navigate to auth signin with returnTo path */
  authWithReturn: (returnTo: string) => 
    buildRouteWithQuery(ROUTES.AUTH, { returnTo }),

  /** Navigate to auth signup with returnTo path */
  authSignupWithReturn: (returnTo: string) => 
    `${ROUTES.AUTH}?tab=signup&returnTo=${encodeURIComponent(returnTo)}`,
  
  /** 
   * Navigate to auth with smart mode detection (signin for returning, signup for new)
   * THIS IS THE PREFERRED METHOD - use this instead of authWithReturn/authSignupWithReturn
   */
  authSmartWithReturn: (returnTo: string) => {
    const isReturning = isReturningVisitor();
    const base = isReturning ? ROUTES.AUTH : ROUTES.AUTH_SIGNUP;
    const separator = base.includes('?') ? '&' : '?';
    return `${base}${separator}returnTo=${encodeURIComponent(returnTo)}`;
  },

  /** Navigate to new publication editor */
  publicationEditorNew: () => ROUTES.ACCOUNT_PUBLICATIONS_NEW,
  
  /** Navigate to new publication editor with topic pre-selected */
  publicationEditorNewWithTopic: (topic: string) => 
    buildRouteWithQuery(ROUTES.ACCOUNT_PUBLICATIONS_NEW, { topic }),
  
  /** Navigate to chat session with project context */
  chatSessionWithProject: (sessionId: string, projectId: string) => 
    `${buildRoute(ROUTES.CHAT_SESSION, { sessionId })}?project=${encodeURIComponent(projectId)}`,
  
  /** Navigate to admin opportunity detail */
  opportunityDetail: (opportunityId: string) => 
    buildRoute(ROUTES.ADMIN_OPERATIONS_OPPORTUNITY_DETAIL, { opportunityId }),
  
  /** Navigate to expert's publication (SEO-friendly URL) */
  expertPublication: (vanityUrl: string, publicationSlug: string) =>
    `/${vanityUrl}/${publicationSlug}`,

  /** Navigate to author's publication via /vanityUrl/publication/slug (media library format) */
  authorPublication: (authorVanityUrl: string, publicationSlug: string) =>
    `/${authorVanityUrl}/publication/${publicationSlug}`,
  
  /** Navigate to public profile by vanity URL */
  publicProfile: (vanityUrl: string) => `/${vanityUrl}`,
  
  /** Navigate to location page by slug */
  locationPage: (slug: string) => `/${slug}`,
  
  /** Navigate to a localized location page (alias for locationPage) */
  localizedLocation: (slug: string) => `/${slug}`,
  
  // REMOVED: localizedLocationRoute - violates routing contract INV-4
  // Global routes must NEVER have location prefix. Use getPath('routeKey') instead.
  
  /** Navigate to unified system entity editor */
  entityEditor: (entityType: string, entityId: string) => 
    buildRoute(ROUTES.ADMIN_MANAGEMENT_ENTITY_EDITOR, { entityType, entityId }),

  /** Navigate to the topic editor page */
  topicEditor: (topicId: string) =>
    buildRoute(ROUTES.ADMIN_TOPIC_EDITOR, { topicId }),
};
